//  Copyright (c) 2019-2023 Cigent Technology Inc. All Rights Reserved.
import {Component, OnInit} from '@angular/core';
import {EncryptionKeyImportStatus, EncryptionService} from '../../../services/encryption.service';
import {MatDialog} from '@angular/material/dialog';
import {UploadKeyDialogComponent} from './upload-key-dialog/upload-key-dialog.component';
import {switchMap} from 'rxjs/operators';
import {EMPTY, forkJoin, Observable} from 'rxjs';
import {HeaderService} from '../../header.service';
import {formatDate} from '@angular/common';
import {NotificationService} from '../../../services/notification-service';
import {TranslateService} from '@ngx-translate/core';
import {KeysDataSource} from './keys-data-source';
import {ExportKeyDialogComponent, IExportKeyDialogResult} from './export-key-dialog/export-key-dialog.component';
import {pushFileToDesktop} from '../encryption/encryption_util';
import {ExportD3eKeysDialogComponent, IExportD3eKeysDialogResult} from './export-d3e-keys-dialog/export-d3e-keys-dialog.component';
import {EditKeyDialogComponent, IEditKeyDialogResult} from './edit-key-dialog/edit-key-dialog.component';
import {IKey} from '../../../types/key';
import {UntilDestroy, untilDestroyed} from '@ngneat/until-destroy';

@UntilDestroy({ checkProperties: true })
@Component({
    selector: 'app-manage-keys',
    templateUrl: './manage-keys.component.html',
    styleUrls: ['./manage-keys.component.scss'],
    standalone: false
})
export class ManageKeysComponent implements OnInit {

  public dataSource!: KeysDataSource;

  columns: string[] = ['name', 'type', 'expirationDate', 'actions'];

  constructor(private readonly encryptionService: EncryptionService,
              private readonly dialog: MatDialog,
              private readonly translate: TranslateService,
              private readonly notificationService: NotificationService,
              private readonly headerService: HeaderService) {
    headerService.showUserFilter(false);
  }

  ngOnInit(): void {
    this.dataSource = new KeysDataSource(this.encryptionService);
    this.dataSource.fetch();
  }

  openUploadDialog() {
    const dialogRef = this.dialog.open(UploadKeyDialogComponent);
    dialogRef.afterClosed().pipe(
      untilDestroyed(this),

      switchMap(result => {
        if (result) {
          let date = null;
          if (result.expirationDate) {
            date = formatDate(result.expirationDate, 'yyyy-MM-dd', 'en', '+00:00')
          }
          return this.encryptionService.importKeyFile(result.name, result.password, result.file, date);
        }
        return EMPTY;
      })
    ).subscribe(response => {
      if (response.status === EncryptionKeyImportStatus.SUCCESS) {
        this.dataSource.fetch();
      } else {
        let message, title;
        switch(response.status) {

          case EncryptionKeyImportStatus.INVALID_FORMAT:
            message = this.translate.instant('key-management.import-failed-message');
            title = this.translate.instant('key-management.import-failed.title')
            break;

          case EncryptionKeyImportStatus.KEY_TOO_SHORT:
            message = this.translate.instant('key-management.import-key-too-short.message');
            title = this.translate.instant('key-management.import-key-too-short.title')
            break;

          case EncryptionKeyImportStatus.UNSUPPORTED_ALGO:
            message = this.translate.instant('key-management.import-key-too-short.message');
            title = this.translate.instant('key-management.import-key-too-short.title')
            break;

          case EncryptionKeyImportStatus.DECRYPT_FAILED:
            message = this.translate.instant('key-management.import-key-too-short.decrypt-failed.message');
            title = this.translate.instant('key-management.import-key-too-short.decrypt-failed.title')
            break;

        }
        this.notificationService.showError(message, title);
      }
    });
  }

  openExportDialog(key): void {
    const dialogRef = this.dialog.open<ExportKeyDialogComponent, any, IExportKeyDialogResult>(ExportKeyDialogComponent, {
      data: key
    });
    dialogRef.afterClosed().pipe(
      untilDestroyed(this),

      switchMap(result => {
        if (result) {
          const observables: {publicKey?: Observable<string>, privateKey?: Observable<string>} = {};
          if (result.exportPublic) {
            observables['publicKey'] = this.encryptionService.getPublicKey(key.id);
          }
          if (result.exportPrivate) {
            if (key.hasProtectedKey) {
              observables['privateKey'] = this.encryptionService.getProtectedKey(key.id);
            }
            if (key.hasPrivateKey) {
              observables['privateKey'] = this.encryptionService.getPrivateKey(key.id, result.password);
            }
          }
          return forkJoin(observables);
        }
        return EMPTY;
      })
    ).subscribe((keys: {publicKey?: string, privateKey?: string}) => {
      const download = (pem: string, filename: string) => {
        const blob = new Blob([pem], {type: 'application/x-pem-file'});
        pushFileToDesktop(blob, filename);
      };
      if (keys.publicKey) {
        download(keys.publicKey, 'public_key.pem');
      }
      if (keys.privateKey) {
        download(keys.privateKey, 'private_key.pem');
      }
    });
  }

  openDownloadDialog() {
    const dialogRef = this.dialog.open<ExportD3eKeysDialogComponent, any, IExportD3eKeysDialogResult>(ExportD3eKeysDialogComponent);
    dialogRef.afterClosed().pipe(
      untilDestroyed(this),

      switchMap(result => {
        if(result) {
          return this.encryptionService.getD3eKeys(result.password);
        }
        return EMPTY;
      })

    ).subscribe(data => {
      const blob = new Blob([data], {type: 'octet/stream'});
      pushFileToDesktop(blob, "cigent_keys.k3y");
    });
  }

  openEditKeyDialog(key) {
    const dialogRef = this.dialog.open<EditKeyDialogComponent, IKey, IEditKeyDialogResult>(EditKeyDialogComponent, {data: key});
    dialogRef.afterClosed().pipe(
      untilDestroyed(this),

      switchMap(result => {
        if (result) {
          return this.encryptionService.putKey(key.id, result.name, result.expires);
        }
        return EMPTY;
      })

    ).subscribe(result => {
      this.dataSource.fetch();
    });
  }

}
